/**
 * MentionInput Component
 * 
 * A textarea/input that supports @mention functionality like Excel/SharePoint.
 * Features:
 * - Type @ to trigger user dropdown
 * - Real-time filtering as you type after @
 * - Keyboard navigation (up/down/enter/escape)
 * - Click to select user
 * - Inserts @email format
 */

import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import ReactDOM from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

const MentionInput = memo(({ 
  value = '',
  onChange,
  onBlur,
  onKeyDown,
  placeholder = 'Add a note... Use @ to mention someone',
  style = {},
  autoFocus = false,
  rows = 3,
  disabled = false
}) => {
  const { instance, accounts } = useMsal();
  const [inputValue, setInputValue] = useState(value);
  const [mentionState, setMentionState] = useState({
    isOpen: false,
    query: '',
    startPos: -1
  });
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  const isSelectingRef = useRef(false); // Flag to prevent blur during selection

  // Sync value prop
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Fetch user suggestions
  const fetchSuggestions = useCallback(async (query) => {
    setLoading(true);
    try {
      const url = `/users/mentions${query ? `?q=${encodeURIComponent(query)}` : ''}`;
      const response = await authenticatedApiCall(instance, accounts, 'GET', url);
      
      if (response.data.success) {
        setSuggestions(response.data.data || []);
        setHighlightedIndex(0);
      }
    } catch (error) {
      console.error('Error fetching mention suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  // Handle input change
  const handleChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    setInputValue(newValue);
    onChange(newValue);

    // Check if we should show mention dropdown
    const textBeforeCursor = newValue.slice(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      // Check if @ is at start or preceded by whitespace
      const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
      
      // Only trigger if @ is at word boundary and no spaces in the query
      if (/\s/.test(charBeforeAt) || lastAtIndex === 0) {
        // Check if there's a space after @ (which would end the mention)
        if (!textAfterAt.includes(' ')) {
          setMentionState({
            isOpen: true,
            query: textAfterAt,
            startPos: lastAtIndex
          });
          
          // Calculate dropdown position
          calculateDropdownPosition();
          
          // Debounce API call
          if (debounceRef.current) {
            clearTimeout(debounceRef.current);
          }
          debounceRef.current = setTimeout(() => {
            fetchSuggestions(textAfterAt);
          }, 150);
          
          return;
        }
      }
    }
    
    // Close dropdown if no valid @ mention
    if (mentionState.isOpen) {
      setMentionState({ isOpen: false, query: '', startPos: -1 });
      setSuggestions([]);
    }
  };

  // Calculate dropdown position based on cursor
  const calculateDropdownPosition = () => {
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const rect = textarea.getBoundingClientRect();
    
    // Position below the textarea
    setDropdownPosition({
      top: rect.bottom + window.scrollY + 2,
      left: rect.left + window.scrollX
    });
  };

  // Handle user selection
  const handleSelect = useCallback((user, e) => {
    // Prevent blur from firing when clicking on dropdown
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Set flag BEFORE any async operations
    isSelectingRef.current = true;
    
    if (!textareaRef.current) {
      isSelectingRef.current = false;
      return;
    }
    
    const { startPos } = mentionState;
    const currentCursorPos = textareaRef.current.selectionStart;
    const beforeMention = inputValue.slice(0, startPos);
    const afterCursor = inputValue.slice(currentCursorPos);
    
    // Insert @email (with space after for easy continuation)
    const insertedMention = `@${user.email} `;
    const newValue = `${beforeMention}${insertedMention}${afterCursor}`;
    
    // Update state immediately
    setInputValue(newValue);
    
    // Close dropdown immediately
    setMentionState({ isOpen: false, query: '', startPos: -1 });
    setSuggestions([]);
    
    // Calculate new cursor position (after the inserted mention + space)
    const newCursorPos = startPos + insertedMention.length;
    
    // Use requestAnimationFrame to ensure DOM is updated before refocusing
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        
        // Call onChange after DOM update to ensure value is synced
        onChange(newValue);
      }
      
      // Reset selection flag after everything is done
      setTimeout(() => {
        isSelectingRef.current = false;
      }, 300);
    });
  }, [inputValue, mentionState, onChange]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (mentionState.isOpen && suggestions.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev => 
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          return;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev => 
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          return;
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          if (suggestions[highlightedIndex]) {
            handleSelect(suggestions[highlightedIndex]);
          }
          return;
        case 'Escape':
          e.preventDefault();
          setMentionState({ isOpen: false, query: '', startPos: -1 });
          setSuggestions([]);
          return;
        default:
          break;
      }
    }
    
    // Pass through to parent handler
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  // Handle blur
  const handleBlur = useCallback((e) => {
    // If we're selecting from dropdown, don't trigger parent blur
    if (isSelectingRef.current) {
      e.preventDefault();
      return;
    }
    
    // Store the event target's value immediately (in case it changes)
    const blurEvent = e;
    
    // Delay to allow click on dropdown to complete
    setTimeout(() => {
      // Double-check we're not in a selection
      if (!isSelectingRef.current) {
        setMentionState({ isOpen: false, query: '', startPos: -1 });
        setSuggestions([]);
        
        if (onBlur) {
          onBlur(blurEvent);
        }
      }
    }, 350); // Increased delay to allow selection to complete
  }, [onBlur]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          textareaRef.current && !textareaRef.current.contains(e.target)) {
        setMentionState({ isOpen: false, query: '', startPos: -1 });
        setSuggestions([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Dropdown component
  const renderDropdown = () => {
    if (!mentionState.isOpen) return null;

    const dropdown = (
      <div
        ref={dropdownRef}
        style={{
          position: 'fixed',
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: '280px',
          maxHeight: '220px',
          backgroundColor: 'white',
          border: '1px solid #dee2e6',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          overflowY: 'auto',
          zIndex: 10001
        }}
      >
        {loading ? (
          <div style={{ 
            padding: '12px', 
            textAlign: 'center', 
            color: '#6c757d',
            fontSize: '0.875rem'
          }}>
            Loading...
          </div>
        ) : suggestions.length > 0 ? (
          suggestions.map((user, index) => (
            <div
              key={user.id}
              onMouseDown={(e) => handleSelect(user, e)}
              onMouseEnter={() => setHighlightedIndex(index)}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                backgroundColor: highlightedIndex === index ? '#e7f3ff' : 'white',
                borderBottom: index < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                transition: 'background-color 0.15s ease'
              }}
            >
              <div style={{ 
                fontWeight: '500', 
                color: '#333',
                fontSize: '0.875rem'
              }}>
                {user.displayName}
              </div>
              <div style={{ 
                fontSize: '0.75rem', 
                color: '#007bff',
                marginTop: '2px'
              }}>
                {user.email}
              </div>
            </div>
          ))
        ) : (
          <div style={{ 
            padding: '12px', 
            textAlign: 'center', 
            color: '#6c757d',
            fontSize: '0.875rem'
          }}>
            {mentionState.query ? 'No users found' : 'Type to search users'}
          </div>
        )}
      </div>
    );

    return ReactDOM.createPortal(dropdown, document.body);
  };

  return (
    <>
      <textarea
        ref={textareaRef}
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        rows={rows}
        style={{
          width: '100%',
          padding: '8px 10px',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          fontSize: '0.875rem',
          resize: 'vertical',
          fontFamily: 'inherit',
          boxSizing: 'border-box',
          backgroundColor: disabled ? '#e9ecef' : 'white',
          ...style
        }}
      />
      {renderDropdown()}
    </>
  );
});

MentionInput.displayName = 'MentionInput';

export default MentionInput;
